import { useState, useRef } from "react";
import { uploadDrawingFile } from "../utils/firebase";
import { Button, Input, Select, Textarea, ProgressBar, cx } from "./UI";
import api from "../utils/api";
import toast from "react-hot-toast";
import { getFileIcon, formatBytes } from "./DrawingViewer";
import { CloudUpload, X } from "lucide-react";

const ALLOWED_EXT = ["dwg", "dxf", "pdf"];
const MAX_SIZE_MB = 100;

export default function DrawingUpload({ siteId, adminId, existingCodes = [], onSuccess, onCancel }) {
  const fileRef = useRef(null);
  const [file, setFile] = useState(null);
  const [form, setForm] = useState({ title: "", description: "", drawingCode: "", tags: "" });
  const [progress, setProgress] = useState(0);
  const [uploading, setUploading] = useState(false);
  const [dragOver, setDragOver] = useState(false);

  const handleFile = (selected) => {
    if (!selected) return;
    const ext = selected.name.split(".").pop().toLowerCase();
    if (!ALLOWED_EXT.includes(ext)) {
      toast.error(`Only ${ALLOWED_EXT.join(", ").toUpperCase()} files are allowed`);
      return;
    }
    if (selected.size > MAX_SIZE_MB * 1024 * 1024) {
      toast.error(`File must be under ${MAX_SIZE_MB} MB`);
      return;
    }
    setFile(selected);
    // Filename makes a decent default title
    if (!form.title) setForm((f) => ({ ...f, title: selected.name.replace(/\.[^.]+$/, "") }));
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    handleFile(e.dataTransfer.files[0]);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!file) return toast.error("Please select a file");
    if (!form.title.trim()) return toast.error("Title is required");

    setUploading(true);
    setProgress(0);
    try {
      const { firebasePath, firebaseURL } = await uploadDrawingFile(file, adminId, siteId, (pct) => setProgress(pct));
      const ext = file.name.split(".").pop().toLowerCase();
      await api.post(`/drawings/site/${siteId}`, {
        title: form.title.trim(),
        description: form.description.trim(),
        fileType: ext,
        fileName: file.name,
        fileSize: file.size,
        firebasePath,
        firebaseURL,
        drawingCode: form.drawingCode || undefined,
        tags: form.tags,
      });
      toast.success("Drawing uploaded successfully!");
      onSuccess && onSuccess();
    } catch (err) {
      toast.error(err?.response?.data?.message || err?.message || "Upload failed");
    } finally {
      setUploading(false);
    }
  };

  const f = (k) => ({ value: form[k], onChange: (e) => setForm({ ...form, [k]: e.target.value }) });

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      {/* ── drop zone ── */}
      <div
        role="button"
        tabIndex={0}
        onClick={() => !uploading && fileRef.current?.click()}
        onKeyDown={(e) => (e.key === "Enter" || e.key === " ") && !uploading && fileRef.current?.click()}
        onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
        onDragLeave={() => setDragOver(false)}
        onDrop={handleDrop}
        className={cx(
          "border-2 border-dashed rounded-2xl p-6 sm:p-8 text-center cursor-pointer transition-all",
          dragOver ? "border-brand-500 bg-brand-50 scale-[1.01]"
            : file ? "border-emerald-300 bg-emerald-50/50"
              : "border-ink-300 hover:border-brand-400 hover:bg-brand-50/40 bg-ink-50/60",
          uploading && "pointer-events-none opacity-70"
        )}
      >
        <input ref={fileRef} type="file" accept=".dwg,.dxf,.pdf" className="hidden"
          onChange={(e) => handleFile(e.target.files[0])} disabled={uploading} />

        {file ? (
          <div className="flex flex-col items-center gap-1.5">
            <span className="text-4xl">{getFileIcon(file.name.split(".").pop())}</span>
            <div className="text-ink-800 font-semibold break-all px-4">{file.name}</div>
            <div className="text-ink-400 text-sm tnum">{formatBytes(file.size)}</div>
            <button type="button" onClick={(e) => { e.stopPropagation(); setFile(null); setProgress(0); }}
              className="inline-flex items-center gap-1 text-xs text-red-600 hover:text-red-700 mt-1 font-medium">
              <X size={12} /> Remove
            </button>
          </div>
        ) : (
          <div className="flex flex-col items-center gap-2">
            <span className="w-12 h-12 rounded-2xl bg-white border border-ink-200 grid place-items-center text-brand-500 shadow-xs">
              <CloudUpload size={22} />
            </span>
            <div className="font-semibold text-ink-700 mt-1">Drop a file here, or tap to browse</div>
            <div className="text-xs text-ink-400">DWG · DXF · PDF — up to {MAX_SIZE_MB} MB</div>
          </div>
        )}
      </div>

      {uploading && (
        <ProgressBar value={progress} label="Uploading…" tone="brand" />
      )}

      <Input label="Drawing Title" required placeholder="e.g. Floor Plan – Level 1" {...f("title")} disabled={uploading} />

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <Select label="Drawing Code" hint="Pick an existing code to upload a new version" {...f("drawingCode")} disabled={uploading}>
          <option value="">New drawing (auto-assign code)</option>
          {existingCodes.map((code) => <option key={code} value={code}>{code} — new version</option>)}
        </Select>
        <Input label="Tags" hint="Comma separated" placeholder="structural, level-1, approved" {...f("tags")} disabled={uploading} />
      </div>

      <Textarea label="Description" rows={2} placeholder="Brief description of this drawing…" {...f("description")} disabled={uploading} />

      <div className="flex flex-col-reverse sm:flex-row sm:justify-end gap-2.5 pt-1">
        <Button type="button" variant="secondary" onClick={onCancel} disabled={uploading} block className="sm:w-auto">Cancel</Button>
        <Button type="submit" loading={uploading} disabled={!file} icon={<CloudUpload size={16} />} block className="sm:w-auto">
          {uploading ? `Uploading ${progress}%` : "Upload Drawing"}
        </Button>
      </div>
    </form>
  );
}
